/**
 * Versie en wijzigingslog van bsmartr (SPEC §10).
 * Bij elke wijziging die live gaat: bovenaan een nieuwe versie toevoegen (semver: x.y.z).
 * - z (patch): fout opgelost;  y (minor): nieuwe functie;  x (major): grote verandering.
 * De nieuwste versie staat bovenaan; VERSIE volgt daaruit.
 */
export interface Wijziging {
  versie: string;
  datum: string; // ISO-datum waarop de versie klaar was
  titel: string;
  punten: string[];
}

export const WIJZIGINGEN: Wijziging[] = [
  {
    versie: '2.16.0',
    datum: '2026-11-05',
    titel: 'bsmartr in Microsoft Teams en single sign-on',
    punten: [
      'bsmartr als app in Microsoft Teams: Start, Mijn taken en het weekrooster in de zijbalk, en vanzelf ingelogd met je Microsoft-account. De beheerder downloadt de app onder Instellingen › Beveiliging.',
      'Inloggen met je werkmail (SSO): het domein van je adres bepaalt waar je inlogt. Ook SAML voor Okta, OneLogin, ADFS en Google Workspace.',
      'Beheerders verifiëren hun e-maildomein en kunnen SSO verplicht maken: medewerkers loggen dan alleen nog in via het bedrijfsaccount.',
      'Rollen uit groepen: wie in een gekoppelde groep zit, krijgt bij het inloggen vanzelf de juiste rol en wordt zo nodig aangemaakt.',
    ],
  },
  {
    versie: '2.15.0',
    datum: '2026-11-04',
    titel: 'Help en handleidingen in de app',
    punten: [
      'Nieuw in je eigen menu: Help en handleidingen. Je opent de handleidingen voor jouw rol als pdf, altijd bij de versie van de app.',
      'De handleidingen zijn bijgewerkt met opmerkingen, @vermeldingen, bijlagen, meldingen, wisselen van organisatie, eigen velden, baselines en kritiek pad, de API en het audit-log.',
    ],
  },
  {
    versie: '2.14.2',
    datum: '2026-11-04',
    titel: 'CSP-meldingen van de website',
    punten: ['Ook de website bsmartr.nl kan CSP-schendingen melden aan de app.'],
  },
  {
    versie: '2.14.1',
    datum: '2026-11-04',
    titel: 'CSP-meldingen',
    punten: ['Browsers melden schendingen van de Content-Security-Policy aan de app (technisch log), en X-XSS-Protection staat op 0 zoals tegenwoordig wordt aanbevolen.'],
  },
  {
    versie: '2.14.0',
    datum: '2026-11-04',
    titel: 'Beveiligingskoppen',
    punten: [
      'De app stuurt nu op elk antwoord beveiligingskoppen mee: Content-Security-Policy, X-Frame-Options, X-Content-Type-Options, Referrer-Policy, Permissions-Policy, Cross-Origin-Opener- en Resource-Policy en een strengere HSTS (2 jaar, ook subdomeinen).',
      'De Outlook-invoegtoepassing blijft werken: die mag in Outlook getoond worden en office.js laden.',
    ],
  },
  {
    versie: '2.13.2',
    datum: '2026-11-03',
    titel: 'Swagger zonder schemameldingen',
    punten: ['Het Swagger-bestand geeft datums als tekst (jjjj-mm-dd) zonder format, zodat de schemavalidatie in Power Automate geen meldingen meer geeft.'],
  },
  {
    versie: '2.13.1',
    datum: '2026-11-03',
    titel: 'API-sleutel makkelijker invullen',
    punten: ['De API-sleutel werkt nu ook zonder “Bearer ” ervoor en met spaties of een regeleinde eromheen, zoals bij het plakken in Power Automate.'],
  },
  {
    versie: '2.13.0',
    datum: '2026-11-03',
    titel: 'Samenwerken, eigen velden, baselines en API',
    punten: [
      'Opmerkingen en bestanden bij taken en projecten. Noem een collega met @: die krijgt een melding bij de nieuwe bel bovenin en een mail.',
      'Eigen velden voor projecten, werkpakketten en taken (tekst, getal, datum, keuzelijst, ja/nee, link), ook als kolom in de lijsten. Instellen bij Instellingen › Eigen velden.',
      'Projectplanning: baseline vastleggen en vergelijken, en per werkpakket de speling. Werkpakketten op het kritieke pad zijn rood. Het projectrapport noemt het verschil met de baseline.',
      'Openbare API (/api/v1) met API-sleutels, een Swagger-bestand voor Power Automate en webhooks voor onder meer nieuwe, gewijzigde en teruggemelde taken. Instellen bij Instellingen › API en webhooks.',
    ],
  },
  {
    versie: '2.12.0',
    datum: '2026-11-03',
    titel: 'Wisselen tussen organisaties',
    punten: [
      'Sta je bij meer dan één organisatie in de gebruikerslijst, dan kies je na het inloggen waar je wilt werken. De keuze wordt onthouden.',
      'Wisselen kan daarna altijd via je gebruikersmenu › Wissel organisatie, zonder opnieuw in te loggen. Rol en gegevens blijven per organisatie gescheiden.',
      'Met een Microsoft-werkaccount kun je alleen kiezen uit organisaties die aan jouw Microsoft-omgeving gekoppeld zijn of nog aan geen enkele.',
      'Elke wissel staat in het audit-log van beide organisaties.',
    ],
  },
  {
    versie: '2.11.2',
    datum: '2026-11-03',
    titel: 'Bezetting terugkijken en rapporttekst',
    punten: [
      'Bezetting toont nu ook de afgelopen 2 weken (hele week), naast 8 weken vooruit. Deze week is gemarkeerd.',
      'De eigen toelichting bij Even bijpraten in het projectrapport heeft altijd hetzelfde lettertype als de rest; geplakte tekst (bijv. uit Word of Outlook) verliest zijn opmaak.',
    ],
  },
  {
    versie: '2.11.1',
    datum: '2026-11-03',
    titel: 'Audit-log onder Instellingen',
    punten: ['Het audit-log staat nu als tabblad bij Beheer › Instellingen, naast Huisstijl en Abonnement, in plaats van als los menu-item.'],
  },
  {
    versie: '2.11.0',
    datum: '2026-11-03',
    titel: 'Audit-log en bewaartermijnen',
    punten: [
      'Nieuw scherm Beheer › Audit-log: wie heeft wat wanneer gedaan, met filters op periode, persoon, actie en zoektekst, en export naar CSV.',
      'Elke wijziging wordt vastgelegd, ook verwijderen, uren en inloggen en uitloggen. Bij wijzigen zie je per veld de oude en de nieuwe waarde.',
      'Wijzigingen tijdens meekijken door platformbeheer staan op naam van platformbeheer, niet op naam van de beheerder.',
      'Het audit-log wordt 12 maanden bewaard. Een beëindigde omgeving (proef verlopen, abonnement opgezegd of onbetaald) wordt 3 maanden na het einde automatisch verwijderd; de datum staat op het abonnementsscherm.',
    ],
  },
  {
    versie: '2.10.1',
    datum: '2026-11-03',
    titel: 'Menu zonder schuifbalk',
    punten: [
      'Het menu links toont geen vaste schuifbalk meer (Windows). Past het menu niet, dan verschijnt een dunne schuifbalk pas als je met de muis over het menu gaat.',
      'Op lagere schermen is het menu compacter, zodat het zonder schuiven past.',
    ],
  },
  {
    versie: '2.10.0',
    datum: '2026-11-03',
    titel: 'Donkere modus en taken als popup',
    punten: [
      'Donkere modus: kies in het gebruikersmenu Licht, Donker of Systeem. Afdrukken blijft licht.',
      'Een nieuwe of bestaande taak opent nu als popup boven het scherm waar je was, net als een nieuw project. Opslaan of annuleren brengt je terug.',
      'Overal echte iconen in plaats van emoji op knoppen en tabbladen.',
    ],
  },
  {
    versie: '2.9.0',
    datum: '2026-11-03',
    titel: 'Nieuw startscherm en nieuwe indeling',
    punten: [
      'Nieuw startscherm per rol: wat er vandaag speelt, mijn projecten met status, actie nodig, capaciteit van het team, afwezig vandaag en livegangen en deadlines in de komende 30 dagen. De projectenlijst staat nu onder Projecten.',
      'Menu aan de linkerkant met iconen, in te klappen. Bovenin het kruimelpad, zoeken en je profiel.',
      'Zoeken met Ctrl+K (of ⌘K): spring direct naar een project, taak, change-nummer of scherm.',
      'Projectenlijst: klik op een project voor een snel overzicht in een zijpaneel. Projectdetail heeft een rustiger knoppenbalk met een ⋯-menu.',
      'Op de telefoon schuift het menu in via ☰; engineers krijgen een tabbalk onderin.',
    ],
  },
  {
    versie: '2.8.0',
    datum: '2026-11-03',
    titel: 'Eigen huisstijl',
    punten: [
      'Nieuw tabblad Beheer › Instellingen › Huisstijl: kies je eigen hoofd- en accentkleur en upload je eigen logo, met een voorbeeld vooraf.',
      'Het eigen logo staat in de bovenbalk en in het projectrapport, met "powered by bsmartr".',
      'Afdrukken krijgen onderaan elke pagina de naam van de organisatie en "powered by bsmartr"; exports naar Excel krijgen een regel "Gemaakt met bsmartr".',
    ],
  },
  {
    versie: '2.7.0',
    datum: '2026-11-03',
    titel: 'Projectrapport',
    punten: [
      'Nieuwe knop "📊 Rapport" bij een project: een statusrapport voor de projectmanager, klaar om te delen, af te drukken of als PDF op te slaan.',
      'Met dagen tot de livegang, een samenvatting in gewone taal, kerncijfers (budget, ingepland, werkpakketten, prognose), de mijlpalen, de voortgang per werkpakket met alle taken of sprint-items, uren per week en per expertise en automatische aandachtspunten.',
      'Je kunt een eigen toelichting typen voor in het rapport.',
    ],
  },
  {
    versie: '2.6.1',
    datum: '2026-11-03',
    titel: 'Nieuw logo',
    punten: [
      'Vernieuwd bsmartr-logo: de cursieve oranje r is iets kleiner en past beter bij de rest van het woord.',
      'Logo als scherpe vectorafbeelding (SVG) in de app, en nieuwe iconen voor browser, telefoon en de Outlook-invoegtoepassing.',
    ],
  },
  {
    versie: '2.6.0',
    datum: '2026-11-02',
    titel: 'Abonnementen, modules en betalen via Stripe',
    punten: [
      'Nieuw tabblad Beheer › Instellingen › Abonnement: plan (Starter, Pro, Enterprise) en modules (Agile, Urenregistratie) kiezen en afsluiten.',
      'Betalen automatisch (iDEAL, creditcard, SEPA-incasso) of op factuur met 30 dagen betaaltermijn, met inkoopnummer en btw-nummer. Facturen, betaalmethode en opzeggen via het klantportaal van Stripe.',
      'Proefperiode van 30 dagen met alle modules; de laatste 7 dagen een melding. Na de proef of bij een achterstallige betaling (na 14 dagen) wordt de toegang geblokkeerd; gegevens blijven bewaard.',
      'Modules die niet in het abonnement zitten zijn verborgen.',
      'Platformbeheer: licentie per organisatie, modules handmatig aan- en uitzetten en de Stripe-prijzen beheren. Bestaande organisaties houden volledige toegang (handmatig contract).',
    ],
  },
  {
    versie: '2.5.0',
    datum: '2026-11-02',
    titel: 'Opgeruimd menu',
    punten: [
      '"Outlook en processen" staat nu als tabblad onder Beheer › Instellingen.',
      'De knop Vernieuwen staat in het gebruikersmenu (👤 › Gegevens vernieuwen); gegevens verversen ook vanzelf na elke wijziging en als je terugkomt in het tabblad.',
    ],
  },
  {
    versie: '2.4.0',
    datum: '2026-11-02',
    titel: 'Tijdlijnweergave in de projectplanning',
    punten: [
      'Projectplanning heeft een knop "Tijdlijn": een overzicht zoals de tijdlijn van MS Project met project, werkpakketten, sprints, feestdagen, vandaag en de mijlpalen (werkpakket gereed, livegang, oplevering).',
      'Af te drukken of als PDF te bewaren, net als de Gantt-tabel.',
    ],
  },
  {
    versie: '2.3.1',
    datum: '2026-11-02',
    titel: 'Demo-login veilig en met de juiste adressen',
    punten: [
      'Het inlogscherm toont de echte adressen van de demo-organisatie.',
      'Beveiliging: zodra inloggen met Microsoft of Google aan staat, werkt de demo-login alleen nog voor demo-accounts (niet meer voor echte e-mailadressen).',
    ],
  },
  {
    versie: '2.3.0',
    datum: '2026-11-02',
    titel: 'Nieuwe demogegevens',
    punten: [
      'Demo met alleen verzonnen namen: engineers, klanten, locaties en contactpersonen.',
      'De demo bevat nu ook een agile project met drie sprints, een backlog, geschreven uren, een sprintrapport en goedgekeurde weken.',
      'Platformbeheer: "Demogegevens opnieuw opbouwen" bij een demo-organisatie; eigen gebruikers blijven behouden.',
    ],
  },
  {
    versie: '2.2.1',
    datum: '2026-11-02',
    titel: 'Mijn uren voor alle rollen',
    punten: ['Mijn uren, het urenrapport en uren goedkeuren openen nu ook voor projectmanagers, planners en beheerders (sprongen terug naar Projecten).'],
  },
  {
    versie: '2.2.0',
    datum: '2026-11-02',
    titel: 'Urenregistratie',
    punten: [
      'Tijd schrijven met een timer (ook in de bovenbalk), met + Tijd of in de weekstaat "Mijn uren"; afgerond op een kwartier.',
      'Bij agile gaan alleen geschreven uren van het projectbudget af; per item zie je geschat, besteed en rest.',
      'Afspraken uit je agenda (Outlook of Google) met één klik als uren; Outlook-invoegtoepassing "bsmartr uren".',
      'Burndown op de rest, urenrapport met Excel-export, facturabel per regel, herinneringen en weekstaten goedkeuren.',
      'De projectmanager krijgt een mail als een item meer dan 25% boven de schatting uitkomt.',
    ],
  },
  {
    versie: '2.1.0',
    datum: '2026-11-01',
    titel: 'Over bsmartr en wijzigingslog',
    punten: [
      'In het gebruikersmenu rechtsboven: "Over bsmartr" met het versienummer en de wijzigingen.',
      'Platformbeheer toont alle versies met de datum waarop ze live gingen.',
    ],
  },
  {
    versie: '2.0.0',
    datum: '2026-11-01',
    titel: 'Nieuwe naam: bsmartr',
    punten: [
      'De app heet bsmartr en draait op https://app.bsmartr.nl.',
      'Mails, agenda-afspraken en teksten gebruiken de nieuwe naam; nieuwe afspraken krijgen de Outlook-categorie "bsmartr".',
      'De database blijft dezelfde, ook als de Portainer-stack anders heet.',
    ],
  },
  {
    versie: '1.5.1',
    datum: '2026-11-01',
    titel: 'Platformbeheer en caching',
    punten: [
      'Platformbeheer telt gebruikers, engineers, projecten en taken per organisatie weer goed.',
      'Na een update laadt de browser altijd de nieuwste versie; het icoon wordt opnieuw opgehaald.',
    ],
  },
  {
    versie: '1.5.0',
    datum: '2026-11-01',
    titel: 'Huisstijl bsmartr',
    punten: [
      'Kleuren navy en oranje, lettertype Open Sans, logo in de bovenbalk.',
      'Nieuw icoon (browser, iPhone/iPad, geïnstalleerde app).',
      'Vernieuwd inlogscherm.',
    ],
  },
  {
    versie: '1.4.0',
    datum: '2026-11-01',
    titel: 'Agile fase 3 en kleine verbeteringen',
    punten: [
      'Schatten in punten per werkpakket; focusblokken in de agenda; backlog uit template; sprintrapport.',
      'Geschat sprintwerk zichtbaar in weekrooster en bezetting.',
      'Getallenkolommen netjes uitgelijnd; knop "Werkzaamheden" bij een project.',
    ],
  },
  {
    versie: '1.3.0',
    datum: '2026-10-29',
    titel: 'Agile fase 2, Teams-kanalen en klantfilter',
    punten: [
      'Agile uren tellen mee in het projectbudget; velocity, burndown, prognose op tempo.',
      'Sprintplanning, review en (optioneel) stand-up automatisch in de agenda.',
      'Teams intern en Teams extern bij een project.',
      'Klantfilter in het weekrooster werkt ook voor projecttaken.',
    ],
  },
  {
    versie: '1.2.0',
    datum: '2026-10-29',
    titel: 'Agile werken per werkpakket',
    punten: [
      'Per werkpakket klassiek (PRINCE2) of agile: backlog, sprints met teamcapaciteit en een bord.',
      'Engineers pakken zelf items op via Mijn taken; de product owner zet ze op Klaar.',
      '"Mijn agenda koppelen" staat in het gebruikersmenu.',
    ],
  },
  {
    versie: '1.1.0',
    datum: '2026-10-28',
    titel: 'Eigen agenda per engineer en mail',
    punten: [
      'Agenda koppelen per engineer: Microsoft (werk of privé) en Google.',
      'Mail via SMTP voor organisaties zonder Microsoft 365.',
      'Bereikbaar via HTTPS.',
    ],
  },
  {
    versie: '1.0.0',
    datum: '2026-10-28',
    titel: 'Eerste SaaS-versie',
    punten: [
      'Projecten, werkpakketten en taken plannen met slimme suggestie, weekrooster met slepen, tijdlijn en bezetting.',
      'Outlook-integratie, terugmelden en verlof.',
      'Meerdere organisaties met platformbeheer.',
    ],
  },
];

export const VERSIE = WIJZIGINGEN[0].versie;
export const VERSIE_DATUM = WIJZIGINGEN[0].datum;
